import React, { ReactNode, useMemo, type JSX } from 'react';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Modal from 'components/UI/Modal';
import Select from 'components/UI/Select';
import { Link } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import InputComponent from 'components/UI/Input';
import Button from 'components/UI/Button';
import { CustomerExtended } from 'data/Customer';
import Vendor from 'data/Vendor';
import Company from 'data/Company';
import { Border, Main, Text } from 'styles/themeColors';
import { TextField, TextFieldProps } from '@mui/material';
import { Autocomplete } from '@mui/material';
import { createCharacterSeparator } from 'utils/string';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';
import { LABELS_TEXT } from 'constants/pricebooks';
import { PricebookCategory, getCategoryLabels } from 'constants/pricebookCategories';
import PricebookCategorySelector from '../components/PricebookCategorySelector';
import InfoIcon from 'components/UI/InfoIcon';
import { Size } from 'lib/styles';

const Label = styled.span<any>`
  font-size: 16px;
  line-height: 20px;

  color: ${({ $hasError }) => $hasError ? Main.error : ""};
`;

const StyledInfoIcon = styled(InfoIcon)`
  display: flex;
  margin-right: ${Size.small};

  svg {
    color: ${Text.placeholder};
  }
`;

const AutocompleteStyled = muiStyled(Autocomplete)({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: Border.main,
  },
  '& .MuiSvgIcon-root': {
    color: Text.primary,
  },
}) as any;

const DEFAULT_USER_OPTION = 'All company members';

// Listbox styles for Autocomplete
const listboxSx = {
  height: 250,
  overflowX: 'hidden'
};

const commaSeparator = createCharacterSeparator({ char: ',', oneWhiteSpace: true });
const atSeparator = createCharacterSeparator({ char: '@', oneWhiteSpace: true, oneSpaceBeforeChar: true });

const filterOptions = (options: any[], { inputValue }: { inputValue: string }) => {
  const searchTerm = inputValue.toLowerCase().trim();
  if (!searchTerm) return options;
  return options.filter((option) =>
    option.value?.toLowerCase().includes(searchTerm)
  );
};

const companyNameNormalizer = (companyName: string): string => {
  // 20 is the maximal length of email address that can be shown without adding whitespaces
  return commaSeparator(companyName.length > 20 ? atSeparator(companyName) : companyName);
}

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm?: (value: string) => void;
  isLoading?: boolean;
  userType?: string;
  selectedCompany?: string | Company;
  assigneeList: Vendor[] | undefined;
  setAssignee: (assignee: Option) => void;
  assignee?: Option;
  companyUsers: CustomerExtended[];
  setCompanyUser: (companyUser: Option) => void;
  companyUser?: Option;
  selectedVendor?: Vendor;
  assignedByError: boolean;
  assignedToError?: boolean;
  companiesList?: Company[];
  setCompany?: (company: Option | undefined) => void;
  selectedBusinessUnit?: BusinessUnit;
  selectedBusinessUnitOption?: Option;
  businessUnitError?: boolean;
  featureFlags: FeatureFlag;
  businessUnitList?: BusinessUnit[];
  setSelectedBusinessUnit?: (businessUnit: Option) => void;
  pricebookCategory: PricebookCategory;
  onSelectCategory: (category: PricebookCategory) => void;
}

interface Option {
  value: string;
  id?: string;
  icon?: ReactNode;
  badge?: any;
}

const NewPricebooksModal = ({
  show,
  onClose,
  onConfirm,
  assigneeList,
  setAssignee,
  assignee,
  selectedCompany,
  companyUsers,
  setCompanyUser,
  companyUser,
  selectedVendor,
  assignedByError,
  assignedToError,
  companiesList,
  setCompany,
  selectedBusinessUnit,
  selectedBusinessUnitOption,
  businessUnitError,
  featureFlags,
  businessUnitList,
  setSelectedBusinessUnit,
  pricebookCategory,
  onSelectCategory
}: Props) => {
  const hasSelectedVendor = !!selectedVendor?.id;
  const linkIconStyle = { height: '25', width: '30', color: Text.primary, margin: '25px 0px 0px 0px' };

  const categoryLabels = getCategoryLabels(pricebookCategory);

  const mappedCompanyUsers = useMemo(() => ([
    {
      value: categoryLabels.DEFAULT_USER_OPTION,
      id: 'All',
    },
    //fix Customer interface (make it have email as array but not string)
    ...(companyUsers || []).filter(Boolean).map((companyUser: any) => ({
      value: companyUser?.email?.[0]?.emailAddress || '',
      id: companyUser?.customerId,
    })).filter(user => user.value).sort((a, b) => a.value.localeCompare(b.value))
  ]), [companyUsers, categoryLabels.DEFAULT_USER_OPTION]);

  const assignedByList = useMemo(() => assigneeList?.map((assignee: any) => ({
    value: assignee.name,
    id: assignee.code,
  })), [assigneeList]);

  const mappedBusinessUnitList = useMemo(() => businessUnitList?.map((businessUnit: any) => ({
    value: businessUnit.name,
    id: businessUnit.code,
    disabled: !!businessUnit.deletedOn,
  })), [businessUnitList]);

  const mappedCompaniesList = useMemo(() => companiesList?.sort((a, b) => a.companyName.localeCompare(b.companyName))?.map((company) => ({
    value: companyNameNormalizer(company.companyName),
    id: company.companyId,
    countryName: company.countryName,
    companyDomain: company.domain
  } as Option)), [companiesList]);

  const selectedCompanyOption = typeof selectedCompany !== 'string' ? {
    value: selectedCompany?.companyName || '',
    id: selectedCompany?.companyId
  } as Option : undefined;

  const isBUSelected = !!selectedBusinessUnit || !!selectedBusinessUnitOption?.value;
  const isCompanySelected = !!selectedCompanyOption?.id;
  const shouldDisableCompanyDropdown = featureFlags?.businessUnit?.enabled && !isBUSelected;
  const shouldDisableCustomerDropdown = shouldDisableCompanyDropdown || (mappedCompaniesList ? !isCompanySelected : false);

  const onSelectCustomer = (event: { target: Option; }) => {
    setCompanyUser(event.target);
  };

  const onSelectAssignee = (event: { target: Option; }) => {
    setAssignee(event.target);
  };

  const onSelectCompany = (event: any, newValue: Option) => {
    setCompany && setCompany(newValue);
  };

  const onBlur = (event: any) => {
    if (!event.target.value.length) {
      setCompany && setCompany(undefined);
    }
  };

  const onSelectBusinessUnit = (event: { target: Option; }) => {
    setSelectedBusinessUnit?.(event.target);
  };

  return (
    <>
      <Modal
        width="794px"
        showModal={show}
        onClose={onClose}
        showButtons={false}
        title={categoryLabels.MODAL_TITLE_COMPANY}
        modalFooterMargin="0"
        left="50vw"
        marginLeft="-397px"
        iconName="PricebookModalIcon"
        showCloseIcon
      >
        <FlexboxContainerComponent flexDirection="column">
          {featureFlags?.affiliateProgram?.enabled && (
            <PricebookCategorySelector
              selectedCategory={pricebookCategory}
              onSelectCategory={onSelectCategory}
            />
          )}
          <FlexboxContainerComponent
            flexDirection="row"
            width="100%"
            padding={featureFlags?.affiliateProgram?.enabled ? '0px' : '90px 0px 0px 0px'}
            alignItems='center'
          >
            {
              (!hasSelectedVendor || (featureFlags?.businessUnit?.enabled && !selectedBusinessUnit)) &&
              <>
                <FlexboxContainerComponent flexDirection="column" width='33%'>
                  {!hasSelectedVendor && (
                    <FlexboxContainerComponent flexDirection="column" width="100%">
                      <FlexboxContainerComponent padding="0px 0px 8px 0">
                        {' '}
                        <Label $hasError={assignedByError}>{LABELS_TEXT.ASSIGN_BY}</Label>
                      </FlexboxContainerComponent>
                      <FlexboxContainerComponent
                        flexDirection="row"
                        alignItems="center"
                        width="100%"
                      >
                        <Select
                          options={assignedByList || []}
                          onChange={onSelectAssignee}
                          value={assignee?.value || ''}
                          defaultValue="Select"
                          width={"100%"}
                          height="36px"
                          isInvalid={assignedByError}
                        />
                      </FlexboxContainerComponent>
                    </FlexboxContainerComponent>
                  )}
                  {
                    featureFlags?.businessUnit?.enabled && !selectedBusinessUnit && (
                      <FlexboxContainerComponent flexDirection="column" width="100%" margin='5px 0px'>
                        <FlexboxContainerComponent padding="0px 0px 8px 0">
                          {' '}
                          <Label $hasError={businessUnitError}>{LABELS_TEXT.BUSINESS_UNIT}</Label>
                        </FlexboxContainerComponent>
                        <FlexboxContainerComponent
                          flexDirection="row"
                          alignItems="center"
                          width="100%"
                        >
                          <Select
                            options={mappedBusinessUnitList || []}
                            onChange={onSelectBusinessUnit}
                            value={selectedBusinessUnitOption?.value || ''}
                            defaultValue="Select"
                            width={"100%"}
                            isInvalid={businessUnitError}
                          />
                        </FlexboxContainerComponent>
                      </FlexboxContainerComponent>
                    )
                  }
                </FlexboxContainerComponent>
                <Link style={linkIconStyle} />
              </>
            }
            <FlexboxContainerComponent flexDirection="row" width={hasSelectedVendor ? '50%' : '33%'}>
              {!mappedCompaniesList ?
                <InputComponent
                  id='assigned-to'
                  labelName={categoryLabels.ASSIGN_TO}
                  labelPosition='top'
                  value={selectedCompany as string}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  }}
                  errorMessage=''
                  name='assigned-to'
                  blocked={true}
                ></InputComponent> :
                <FlexboxContainerComponent flexDirection="column" width="100%">
                  <FlexboxContainerComponent padding="0px 0px 8px 0">
                    {' '}
                    <Label $hasError={assignedToError}>{categoryLabels.ASSIGN_TO}</Label>
                  </FlexboxContainerComponent>
                  <FlexboxContainerComponent
                    flexDirection="row"
                    alignItems="center"
                    width="100%"
                  >

                    <AutocompleteStyled
                      filterOptions={filterOptions}
                      slotProps={{ listbox: { sx: listboxSx } }}
                      id="assigned-to"
                      options={mappedCompaniesList}
                      disabled={shouldDisableCompanyDropdown}
                      getOptionKey={(option: any) => option.id}
                      getOptionLabel={(option: any) => option.value}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      style={{
                        width: `100%`
                      }}
                      value={selectedCompanyOption}
                      disableClearable={true}
                      onChange={onSelectCompany}
                      onBlur={onBlur}
                      renderInput={AutoCompleteInput}
                      renderOption={CompanyOptionItem}
                    />
                  </FlexboxContainerComponent>
                </FlexboxContainerComponent>
              }
            </FlexboxContainerComponent>
            <FlexboxContainerComponent
              flexDirection="row"
              alignItems="center"
              width={hasSelectedVendor ? '50%' : '33%'}
              padding="0px 0px 0px 4px"
            >
              <Select
                label={categoryLabels.COMPANY_USER}
                labelPosition="top"
                options={mappedCompanyUsers}
                onChange={onSelectCustomer}
                value={companyUser ? companyUser.value : categoryLabels.DEFAULT_USER_OPTION}
                defaultValue="Select"
                width="100%"
                disabled={shouldDisableCustomerDropdown}
              />
            </FlexboxContainerComponent>
          </FlexboxContainerComponent>

          {pricebookCategory === PricebookCategory.AFFILIATE && (
            <FlexboxContainerComponent padding="16px 0 0 0" flexDirection="row" alignItems="center">
              <StyledInfoIcon tooltipText="You can assign this pricebook to a specific affiliate user or to all users within the selected affiliate company." />
              <span style={{ fontSize: '14px', color: Text.placeholder, fontStyle: 'italic' }}>
                You can assign this pricebook to a specific affiliate user or to all users within the selected affiliate company.
              </span>
            </FlexboxContainerComponent>
          )}

          <FlexboxContainerComponent
            padding="104px 0 0 0"
            justifyContent="flex-end"
          >
            <Button id="modal-main-button" bgColor={Main.brandDark} onClick={onConfirm}>
              Next
            </Button>
          </FlexboxContainerComponent>
        </FlexboxContainerComponent>
      </Modal>
    </>);
};

export default NewPricebooksModal;

const AutoCompleteInput = (params: JSX.IntrinsicAttributes & TextFieldProps) => {
  return <TextField
    {...params}
    variant='outlined'
    inputProps={{
      ...params.inputProps,
      style: {
        padding: 0,
      },
      placeholder: 'Start typing company name',
    }}
  />
};

const CompanyOptionItem = (props: React.HTMLAttributes<HTMLLIElement>, option: any) => (
  <li {...props}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
      <div
        style={
          { fontSize: '14px', fontWeight: 'bold' }
        }>
        {option.value}
      </div>
      <div style={{ fontSize: '12px', fontStyle: 'italic', color: Text.placeholder }}>
        {option.countryName} - @{option.companyDomain}
      </div>
    </div>
  </li>
)